import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Skeleton from '@material-ui/lab/Skeleton';

// CONSTANTS
import { ROUTE_SHOWS_AND_MOVIES_SHOW } from '@/constants/routes';

// UTILITY
import { updateCarouselOptions } from '@/utility/content';
import { getLink, routeEnhance } from '@/utility/routes';

// STORE
import { ContentActions } from '@/store/actions';

// HOOKS
import useActions from '@/hooks/useActions';

// COMPONENTS
import SectionTitleLink from '@/components/SectionTitleLink';
import StageCarousel from '@/components/Carousels/StageSwiperCarousel';
import { SwiperSlide } from '@/components/Carousels/SwiperCarousel';
import EpisodeCard from '@/components/Cards/EpisodeCard';
import EpisodeCardSkeleton from '@/components/Skeletons/EpisodeCardSkeleton';

// STYLES
import { Container, CarouselWrapper } from './styles';

const carouselOptions = {
  spaceBetween: 30,
  slidesPerView: 'auto',
  watchOverflow: true,
  breakpoints: {
    0: {
      speed: 600,
      slidesPerGroup: 3.2,
      spaceBetween: 8,
    },
    600: {
      speed: 600,
      slidesPerGroup: 3,
    },
    1200: {
      speed: 1000,
      slidesPerGroup: 3,
      allowTouchMove: false,
    },
  },
};

const EpisodesCarousel = props => {
  const {
    data,
    title,
    link,
    stylesType,
    loading,
    analyticsLocation,
    swimlaneAnalytics,
    onUpdateStatus,
    hideTitle,
    hideMobileNav,
  } = props;
  const navigate = useNavigate();
  const { isAuth, partner } = useSelector(({ partner: partnerStore, auth }) => ({
    partner: partnerStore.data,
    isAuth: auth.isAuth,
  }));

  const [changeContentStatus] = useActions([
    (content_id, status, callback) => {
      return ContentActions.changeContentStatus.action({
        content_id,
        status,
        callback,
      });
    },
  ]);

  const [state, setState] = useState({
    displayDialog: false,
    selectedId: -1,
    selectedData: {},
    carouselOptions: hideMobileNav ? updateCarouselOptions(carouselOptions) : carouselOptions,
    card_index: null,
  });

  const downMd = useMediaQuery(({ breakpoints }) => breakpoints.down('md'));
  const downSm = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));

  React.useEffect(() => {
    if (hideMobileNav) {
      setState({ ...state, carouselOptions: updateCarouselOptions(carouselOptions) });
    }
  }, [hideMobileNav]);

  const hasSwimlaneAnalytics = !!Object.keys(swimlaneAnalytics).length;

  let titleLinkProps = {};

  if (hasSwimlaneAnalytics) {
    titleLinkProps = {
      analyticsType: 'swimlane-see-all',
      analyticsProps: {
        ...swimlaneAnalytics,
        type: 'title',
      },
    };
  }

  let updatedStylesType = stylesType;

  function handleChangeContentStatus(id, contentStatus) {
    changeContentStatus(id, contentStatus, () => {
      onUpdateStatus(id, contentStatus);
    });
  }

  const onCardClick = cardData => {
    const { content } = cardData;
    const showUrl = getLink(ROUTE_SHOWS_AND_MOVIES_SHOW, {
      show_id: content.slug,
    });
    const routeEnhanced = routeEnhance(`${showUrl}#episodes`, {
      state: { location: analyticsLocation },
    });

    navigate(routeEnhanced);
  };

  if (downMd && updatedStylesType === 'collections' && !hideMobileNav) {
    updatedStylesType = 'want-to-watch';
  }

  const skeletons = new Array(downSm ? 3 : 6).fill('');
  const skeletonsSection = (
    <>
      {!hideTitle && (
        <Grid item xs={12}>
          <Skeleton width="40%" height={50} />
        </Grid>
      )}
      {skeletons.map((item, index) => {
        return (
          <Grid item xs={4} key={index}>
            <EpisodeCardSkeleton />
          </Grid>
        );
      })}
    </>
  );

  return (
    <Container className={clsx('carousel-episodes', `carousel-episodes--${updatedStylesType}`)}>
      {loading ? (
        <Grid container spacing={2}>
          {skeletonsSection}
        </Grid>
      ) : (
        <>
          {data.length ? (
            <>
              {!!title && !hideTitle && (
                <SectionTitleLink
                  className="carousel-episodes__title"
                  title={title}
                  link={link}
                  analyticsLocation={analyticsLocation}
                  LinkProps={titleLinkProps}
                />
              )}
              {data.length && (
                <CarouselWrapper>
                  <StageCarousel stylesType={updatedStylesType} carouselOptions={carouselOptions}>
                    {data.map((appData, card_index) => (
                      <SwiperSlide key={card_index}>
                        <EpisodeCard
                          isAuth={isAuth}
                          data={appData}
                          changeContentStatus={handleChangeContentStatus}
                          onClick={onCardClick}
                          partner={partner}
                          analyticsLocation={analyticsLocation}
                          swimlaneAnalytics={{ ...swimlaneAnalytics, card_index }}
                        />
                      </SwiperSlide>
                    ))}
                  </StageCarousel>
                </CarouselWrapper>
              )}
            </>
          ) : null}
        </>
      )}
    </Container>
  );
};

EpisodesCarousel.defaultProps = {
  data: [],
  title: '',
  link: '',
  stylesType: 'want-to-watch',
  loading: false,
  swimlaneAnalytics: {},
  onUpdateStatus: () => {},
  hideTitle: false,
  hideMobileNav: false,
};

EpisodesCarousel.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
  link: PropTypes.string,
  analyticsLocation: PropTypes.string,
  stylesType: PropTypes.string,
  loading: PropTypes.bool,
  swimlaneAnalytics: PropTypes.object,
  onUpdateStatus: PropTypes.func,
  hideTitle: PropTypes.bool,
  hideMobileNav: PropTypes.bool,
};

export default React.memo(EpisodesCarousel);
